/**
 * Support Tickets API Client
 *
 * Covers tickets, comments, SLA policies, escalation rules, and trends.
 */

const BASE = '/api/support';

export type TicketCategory = 'billing' | 'technical' | 'account' | 'other';
export type TicketPriority = 'low' | 'medium' | 'high' | 'critical';
export type TicketStatus =
  | 'open'
  | 'in_progress'
  | 'waiting_on_customer'
  | 'resolved'
  | 'closed';

export interface AttachmentMeta {
  id: string;
  filename: string;
  size: number;
  created_at: string;
  url?: string;
}

// ── SLA types ─────────────────────────────────────────────────────────────────

export interface SLAStatus {
  policy: { id: string; name: string };
  first_response_target: string;
  first_response_met: boolean | null;
  resolution_target: string;
  resolution_met: boolean | null;
  time_remaining_minutes: number | null;
  breached: boolean;
}

export interface SLAPriorityTarget {
  id: string;
  sla_policy_id: string;
  priority: TicketPriority;
  first_response_minutes: number;
  resolution_minutes: number;
  business_hours_only: boolean;
}

export interface SLAPolicy {
  id: string;
  name: string;
  description: string | null;
  is_default: boolean;
  org_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface SLAPolicyWithTargets extends SLAPolicy {
  priority_targets: SLAPriorityTarget[];
  escalation_rules: EscalationRule[];
}

export type EscalationTrigger = 'time_based' | 'severity_based' | 'ai_confidence_based';
export type EscalationActionType = 'reassign' | 'notify' | 'change_priority' | 'page';

export interface EscalationRule {
  id: string;
  sla_policy_id: string;
  trigger_type: EscalationTrigger;
  condition: Record<string, unknown>;
  action_type: EscalationActionType;
  action_config: Record<string, unknown>;
  enabled: boolean;
  sort_order: number;
  created_at: string;
}

export interface SLAImpact {
  first_response_recorded: boolean;
  sla_status_updated: boolean;
}

export interface TrendsResult {
  period: string;
  granularity: string;
  sla_compliance_rate: number;
  total_resolved: number;
  compliant_count: number;
}

// ── Ticket types ──────────────────────────────────────────────────────────────

export interface Ticket {
  id: string;
  subject: string;
  description: string;
  category: TicketCategory;
  priority: TicketPriority;
  status: TicketStatus;
  reporter_id: string;
  assignee_id: string | null;
  version: number;
  sla_due_at: string | null;
  resolved_at: string | null;
  sla_policy_id: string | null;
  sla_first_response_at: string | null;
  sla_resolution_at: string | null;
  sla_breached: boolean;
  created_at: string;
  updated_at: string;
  attachments: AttachmentMeta[];
  /** Computed SLA status, present in ticket detail responses. */
  sla_status?: SLAStatus | null;
}

export interface Comment {
  id: string;
  ticket_id: string;
  author_id: string;
  body: string;
  is_internal: boolean;
  author_type: 'agent' | 'customer';
  created_at: string;
}

export interface CommentWithSLAImpact {
  comment: Comment;
  sla_impact: SLAImpact;
}

export interface ListTicketsParams {
  status?: TicketStatus;
  priority?: TicketPriority;
  assignee_id?: string;
  created_after?: string;
  created_before?: string;
  cursor?: string;
  limit?: number;
  sla_breached?: string; // 'true' | 'false'
}

export interface ListTicketsResult {
  tickets: (Ticket & { sla_breached: boolean })[];
  next_cursor: string | null;
}

export interface ListCommentsResult {
  comments: Comment[];
  next_cursor: string | null;
}

// ── API helpers ───────────────────────────────────────────────────────────────

class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function apiFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new ApiError(res.status, (body as { error?: string }).error ?? res.statusText);
  }
  const json = (await res.json()) as { data: T };
  return json.data;
}

// ── Support API ───────────────────────────────────────────────────────────────

export const supportApi = {
  /** Create a new support ticket (multipart when attachments present). */
  async createTicket(
    payload: {
      subject: string;
      description: string;
      category: TicketCategory;
      priority: TicketPriority;
    },
    attachments?: File[],
  ): Promise<Ticket> {
    if (attachments && attachments.length > 0) {
      const form = new FormData();
      Object.entries(payload).forEach(([k, v]) => form.append(k, v));
      attachments.forEach((f) => form.append('attachments', f));

      const res = await fetch(`${BASE}/tickets`, {
        method: 'POST',
        body: form,
        credentials: 'include',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }));
        throw new ApiError(res.status, (body as { error?: string }).error ?? res.statusText);
      }
      const json = (await res.json()) as { data: Ticket };
      return json.data;
    }

    return apiFetch<Ticket>(`${BASE}/tickets`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /** List tickets with optional filters. */
  async listTickets(params: ListTicketsParams = {}): Promise<ListTicketsResult> {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    });
    const query = qs.toString() ? `?${qs}` : '';
    return apiFetch<ListTicketsResult>(`${BASE}/tickets${query}`);
  },

  /** Get a single ticket by ID (includes presigned attachment URLs and SLA status). */
  async getTicket(id: string): Promise<Ticket> {
    return apiFetch<Ticket>(`${BASE}/tickets/${id}`);
  },

  /** Update a ticket. */
  async updateTicket(
    id: string,
    patch: {
      status?: TicketStatus;
      priority?: TicketPriority;
      description?: string;
      assignee_id?: string | null;
      version?: number;
    },
  ): Promise<Ticket> {
    return apiFetch<Ticket>(`${BASE}/tickets/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },

  /** Transition ticket status (dedicated endpoint with SLA side effects). */
  async transitionStatus(
    id: string,
    status: TicketStatus,
    reason?: string,
  ): Promise<Ticket> {
    return apiFetch<Ticket>(`${BASE}/tickets/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, reason }),
    });
  },

  /** Add a comment to a ticket. Returns comment + SLA impact. */
  async addComment(
    ticketId: string,
    body: string,
    is_internal = false,
  ): Promise<CommentWithSLAImpact> {
    return apiFetch<CommentWithSLAImpact>(`${BASE}/tickets/${ticketId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ body, is_internal }),
    });
  },

  /** List comments for a ticket. */
  async listComments(ticketId: string, cursor?: string): Promise<ListCommentsResult> {
    const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    return apiFetch<ListCommentsResult>(`${BASE}/tickets/${ticketId}/comments${qs}`);
  },

  /** Get SLA compliance trends. */
  async getTrends(period = '30d', granularity = 'day'): Promise<TrendsResult> {
    return apiFetch<TrendsResult>(
      `${BASE}/tickets/trends?period=${period}&granularity=${granularity}`,
    );
  },

  // ── SLA Policies ────────────────────────────────────────────────────────────

  async listSLAPolicies(orgId?: string): Promise<{ policies: SLAPolicy[] }> {
    const qs = orgId ? `?org_id=${encodeURIComponent(orgId)}` : '';
    return apiFetch<{ policies: SLAPolicy[] }>(`${BASE}/sla-policies${qs}`);
  },

  async getSLAPolicy(id: string): Promise<SLAPolicyWithTargets> {
    return apiFetch<SLAPolicyWithTargets>(`${BASE}/sla-policies/${id}`);
  },

  async createSLAPolicy(payload: {
    name: string;
    description?: string;
    org_id?: string;
    is_default?: boolean;
    priority_targets: Array<{
      priority: TicketPriority;
      first_response_minutes: number;
      resolution_minutes: number;
      business_hours_only?: boolean;
    }>;
  }): Promise<SLAPolicyWithTargets> {
    return apiFetch<SLAPolicyWithTargets>(`${BASE}/sla-policies`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async updateSLAPolicy(
    id: string,
    patch: { name?: string; description?: string; is_default?: boolean },
  ): Promise<SLAPolicy> {
    return apiFetch<SLAPolicy>(`${BASE}/sla-policies/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },

  async deleteSLAPolicy(id: string): Promise<void> {
    await fetch(`${BASE}/sla-policies/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
  },

  // ── Escalation Rules ─────────────────────────────────────────────────────────

  async listEscalationRules(slaPolicyId?: string): Promise<{ rules: EscalationRule[] }> {
    const qs = slaPolicyId ? `?sla_policy_id=${encodeURIComponent(slaPolicyId)}` : '';
    return apiFetch<{ rules: EscalationRule[] }>(`${BASE}/escalation-rules${qs}`);
  },

  async createEscalationRule(payload: {
    sla_policy_id: string;
    trigger_type: EscalationTrigger;
    condition: Record<string, unknown>;
    action_type: EscalationActionType;
    action_config: Record<string, unknown>;
    sort_order?: number;
    enabled?: boolean;
  }): Promise<EscalationRule> {
    return apiFetch<EscalationRule>(`${BASE}/escalation-rules`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async updateEscalationRule(
    id: string,
    patch: Partial<{
      trigger_type: EscalationTrigger;
      condition: Record<string, unknown>;
      action_type: EscalationActionType;
      action_config: Record<string, unknown>;
      sort_order: number;
      enabled: boolean;
    }>,
  ): Promise<EscalationRule> {
    return apiFetch<EscalationRule>(`${BASE}/escalation-rules/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },

  async deleteEscalationRule(id: string): Promise<void> {
    await fetch(`${BASE}/escalation-rules/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
  },
};
